// src/pages/PaymentPage.jsx
import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Footer } from "../components/common";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import "../styles/CartScreen.css";
import "../styles/OrderConfirmation.css";
import "../styles/PaymentPage.css";
import http from "../lib/http";
import { toast } from "react-toastify";
import { getAuthMode } from "../utils/authHelpers";

const toCurrency = (n) => `$${Number(n || 0).toFixed(2)}`;
const getApiMessage = (err, fallback = "Something went wrong") =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  err?.message ||
  fallback;

const PAYMENT_METHODS = [
  {
    id: "paypal",
    label: "PayPal",
    description: "Pay securely with your PayPal account",
    color: "#0070ba",
    bg: "#f0f7ff",
    logo: (
      <svg
        viewBox="0 0 100 32"
        width="72"
        height="24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <text
          y="24"
          fontFamily="Arial,sans-serif"
          fontWeight="900"
          fontSize="26"
          fill="#003087"
        >
          Pay
        </text>
        <text
          x="42"
          y="24"
          fontFamily="Arial,sans-serif"
          fontWeight="900"
          fontSize="26"
          fill="#009cde"
        >
          Pal
        </text>
      </svg>
    ),
  },
  {
    id: "stripe",
    label: "Credit / Debit Card",
    description: "Visa, Mastercard, Amex — powered by Stripe",
    color: "#635bff",
    bg: "#f5f4ff",
    logo: (
      <svg
        viewBox="0 0 68 26"
        width="54"
        height="22"
        xmlns="http://www.w3.org/2000/svg"
      >
        <text
          y="20"
          fontFamily="Arial,sans-serif"
          fontWeight="900"
          fontSize="22"
          fill="#635bff"
        >
          stripe
        </text>
      </svg>
    ),
  },
];

// ── Confirmation screen ────────────────────────────────
function OrderConfirmationScreen({
  orderData,
  paymentData,
  user,
  guestContact,
  navigate,
  isGuest,
}) {
  const displayName =
    user?.full_name || user?.name || guestContact?.contact_name || "Guest";
  const displayEmail = user?.email || guestContact?.contact_email || "";

  return (
    <div className="order-confirmation">
      <div className="confirmation-container">
        <div className="confirmation-content">
          <div className="thank-you-section">
            <div className="pp-success-icon">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#fff"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h1 className="thank-you-title">Thank you for your purchase!</h1>
            <p className="thank-you-subtitle">
              Your order will be processed within 24 hours during working days.
              We will notify you by email once your order has been shipped.
            </p>
            <div className="billing-section">
              <h3 className="billing-title">Billing Details</h3>
              <div className="billing-details">
                <div className="billing-row">
                  <span className="billing-label">Name</span>
                  <span className="billing-value">{displayName}</span>
                </div>
                <div className="billing-row">
                  <span className="billing-label">Email</span>
                  <span className="billing-value">{displayEmail}</span>
                </div>
                {paymentData?.status && (
                  <div className="billing-row">
                    <span className="billing-label">Payment Status</span>
                    <span
                      className="billing-value pp-status-badge"
                      data-status={paymentData.status}
                    >
                      {paymentData.status === "success"
                        ? "✅ Paid"
                        : paymentData.status === "processing"
                          ? "⏳ Processing"
                          : paymentData.status}
                    </span>
                  </div>
                )}
              </div>

              {/* ── Guest → home | Auth → orders ── */}
              {isGuest ? (
                <div className="pp-guest-confirm-actions">
                  <p className="pp-guest-confirm-note">
                    📧 A confirmation will be sent to{" "}
                    <strong>{displayEmail}</strong>
                  </p>
                  <button
                    className="track-order-btn"
                    onClick={() => navigate("/")}
                  >
                    Continue Shopping
                  </button>
                </div>
              ) : (
                <button
                  className="track-order-btn"
                  onClick={() => navigate("/orders")}
                >
                  Track Your Order
                </button>
              )}
            </div>
          </div>

          <div className="order-summary-section">
            <h2 className="order-summary-title">Order Summary</h2>
            <div className="order-info-grid">
              <div className="order-info-item">
                <div className="order-info-label">Date</div>
                <div className="order-info-value">{orderData.date}</div>
              </div>
              <div className="order-info-item">
                <div className="order-info-label">Order Number</div>
                <div className="order-info-value">
                  {orderData.order_number || orderData.order_no || "—"}
                </div>
              </div>
              <div className="order-info-item">
                <div className="order-info-label">Payment</div>
                <div
                  className="order-info-value"
                  style={{ textTransform: "capitalize" }}
                >
                  {orderData.payment_method === "stripe"
                    ? "Card (Stripe)"
                    : "PayPal"}
                </div>
              </div>
            </div>

            {(orderData.items || []).map((item, idx) => (
              <div key={idx} className="order-product">
                <img
                  src={
                    item.image ||
                    item.product_image ||
                    "/placeholder-product.jpg"
                  }
                  alt={item.name || item.product_name || "Product"}
                  className="order-product-image"
                />
                <div className="order-product-details">
                  <h4 className="order-product-name">
                    {item.name || item.product_name || "Product"}
                  </h4>
                  <p className="order-product-qty">Qty: {item.quantity || 1}</p>
                </div>
                <div className="order-product-price">
                  {toCurrency(
                    Number(item.price || 0) * Number(item.quantity || 1),
                  )}
                </div>
              </div>
            ))}

            <div className="order-totals">
              <div className="order-total-row">
                <span className="order-total-label">Sub Total</span>
                <span className="order-total-value">
                  {toCurrency(orderData.totals?.itemsTotal)}
                </span>
              </div>
              <div className="order-total-row">
                <span className="order-total-label">Shipping</span>
                <span className="order-total-value">
                  {toCurrency(orderData.totals?.deliveryFee)}
                </span>
              </div>
              {orderData.totals?.promoDiscount > 0 && (
                <div className="order-total-row">
                  <span className="order-total-label">Promo Discount</span>
                  <span
                    className="order-total-value"
                    style={{ color: "#4caf50" }}
                  >
                    -{toCurrency(orderData.totals.promoDiscount)}
                  </span>
                </div>
              )}
              {orderData.totals?.shippingVoucherDiscount > 0 && (
                <div className="order-total-row">
                  <span className="order-total-label">Voucher Discount</span>
                  <span
                    className="order-total-value"
                    style={{ color: "#2196f3" }}
                  >
                    -{toCurrency(orderData.totals.shippingVoucherDiscount)}
                  </span>
                </div>
              )}
              <div className="order-total-row final">
                <span className="order-total-label final">Order Total</span>
                <span className="order-total-value final">
                  {toCurrency(orderData.totals?.total)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────
const PaymentPage = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const authMode = getAuthMode();
  const isGuest = authMode === "guest";
  const isUser = authMode === "auth" || authMode === "user";

  const items = Array.isArray(state?.items) ? state.items : [];
  const totalsFromCheckout = state?.totals;
  const shippingAddressId = state?.shippingAddressId;
  const selectedPromoCode = state?.selectedPromoCode || null;
  const selectedVoucher = state?.selectedVoucher || null;

  const guestOrder = state?.guestOrder || null;
  const guestPayload = state?.guestPayload || null;
  const guestContact = guestPayload
    ? {
        contact_name: guestPayload.contact_name,
        contact_email: guestPayload.contact_email,
        contact_phone: guestPayload.contact_phone,
      }
    : null;

  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("paypal");
  const [orderConfirmed, setOrderConfirmed] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [polling, setPolling] = useState(false);

  // ── Track whether the CONFIRMED order was a guest order ──
  // Must be read from sessionStorage pending data (not from current authMode)
  // because after redirect back the React state is fresh
  const [confirmedIsGuest, setConfirmedIsGuest] = useState(false);
  const [confirmedGuestContact, setConfirmedGuestContact] = useState(null);

  const computedTotals = useMemo(() => {
    if (totalsFromCheckout) return totalsFromCheckout;
    const itemsTotal = items.reduce(
      (s, i) => s + Number(i.price || 0) * Number(i.quantity || 1),
      0,
    );
    const deliveryFee = items.length > 0 ? 10 : 0;
    return {
      itemsTotal,
      deliveryFee,
      total: itemsTotal + deliveryFee,
      promoDiscount: 0,
      shippingVoucherDiscount: 0,
    };
  }, [items, totalsFromCheckout]);

  const getGuestHeaders = () => {
    const token = localStorage.getItem("guestToken");
    return token ? { "X-Guest-Token": token } : {};
  };

  // ── Poll payment status ────────────────────────────────
  const pollPaymentStatus = useCallback(
    async (paymentId, pending) => {
      setPolling(true);
      let attempts = 0;
      const maxAttempts = 8;

      // ── Read isGuest from pending data — NOT from current authMode ──
      // After a redirect back, authMode is re-evaluated from localStorage.
      // The pending object has the ground truth from when Pay was clicked.
      const isPendingGuest = pending?.isGuest === true;
      const pollUrl = isPendingGuest
        ? `/api/guest/payments/${paymentId}`
        : `/api/customer/payments/${paymentId}`;
      const pollHeaders = isPendingGuest ? getGuestHeaders() : {};

      const buildOrderData = () => ({
        order_number:
          pending?.createdOrder?.order_no || `ORD-${pending?.orderId}`,
        date: new Date().toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        }),
        items: pending?.items || [],
        totals: pending?.totals || {},
        payment_method: pending?.paymentMethod || "stripe",
      });

      const check = async () => {
        attempts++;
        try {
          const res = await http.get(pollUrl, { headers: pollHeaders });
          const data = res?.data?.data || res?.data;
          const status = data?.status || data?.payment?.status;

          if (status === "success") {
            setPaymentData(data);
            setOrderData(buildOrderData());
            // ── Set confirmed guest state from pending ──
            setConfirmedIsGuest(isPendingGuest);
            setConfirmedGuestContact(pending?.guestContact || null);
            setOrderConfirmed(true);
            setPolling(false);
            sessionStorage.removeItem("pending_payment");
            toast.success("Payment confirmed! 🎉");
            return;
          }

          if (status === "cancelled" || status === "failed") {
            setPolling(false);
            sessionStorage.removeItem("pending_payment");
            toast.error(`Payment ${status}.`);
            navigate("/");
            return;
          }

          if (attempts < maxAttempts) {
            setTimeout(check, 2000);
          } else {
            // Webhook hasn't fired yet — show confirmation anyway
            setPolling(false);
            setPaymentData({ status: "processing" });
            setOrderData(buildOrderData());
            setConfirmedIsGuest(isPendingGuest);
            setConfirmedGuestContact(pending?.guestContact || null);
            setOrderConfirmed(true);
            sessionStorage.removeItem("pending_payment");
            toast.info(
              "Payment is being processed. Your order will update shortly.",
            );
          }
        } catch {
          setPolling(false);
        }
      };

      check();
    },
    [navigate],
  );

  // ── Handle return from Stripe / PayPal ────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentId = params.get("payment_id");
    const sessionId = params.get("session_id");
    const token = params.get("token");
    const payerId = params.get("PayerID");
    const status = params.get("status");

    const pending = (() => {
      try {
        return JSON.parse(sessionStorage.getItem("pending_payment") || "null");
      } catch {
        return null;
      }
    })();

    // ── Stripe return ──
    if (paymentId && sessionId) {
      pollPaymentStatus(paymentId, pending);
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }

    // ── Stripe cancel ──
    if (paymentId && !sessionId && !token) {
      sessionStorage.removeItem("pending_payment");
      toast.info("Payment was cancelled.");
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }

    // ── PayPal return ──
    if (token || payerId || status === "success") {
      if (pending) {
        const isPendingGuest = pending?.isGuest === true;
        setOrderData({
          order_number:
            pending.createdOrder?.order_no || `ORD-${pending.orderId}`,
          date: new Date().toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          }),
          items: pending.items || [],
          totals: pending.totals || {},
          payment_method: "paypal",
        });
        // ── Set confirmed guest state from pending ──
        setConfirmedIsGuest(isPendingGuest);
        setConfirmedGuestContact(pending?.guestContact || null);
        setOrderConfirmed(true);
        sessionStorage.removeItem("pending_payment");
        toast.success("Payment completed! 🎉");
      }
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [pollPaymentStatus]);

  // ── Initiate payment ──────────────────────────────────
  const initiateGuestPayment = async (orderId) => {
    const headers = getGuestHeaders();
    if (!headers["X-Guest-Token"])
      throw new Error("Guest session expired. Please refresh.");

    if (paymentMethod === "stripe") {
      const res = await http.post(
        "/api/guest/payments/stripe/initiate",
        { order_ids: [orderId], payment_method: "stripe" },
        { headers },
      );
      const link = res?.data?.payment_link || res?.data?.data?.payment_link;
      if (!link) throw new Error("Stripe payment link not received");
      return { link, payment: res?.data?.payment };
    } else {
      const res = await http.post(
        "/api/guest/payments/initiate",
        { order_ids: [orderId], payment_method: "paypal" },
        { headers },
      );
      const data = res?.data?.data || res?.data;
      const link =
        data?.payment_link || res?.data?.payment_link || data?.approval_url;
      if (!link) throw new Error("PayPal payment link not received");
      return { link, payment: res?.data?.payment || data?.payment };
    }
  };

  const initiateAuthPayment = async (orderId) => {
    if (paymentMethod === "stripe") {
      const res = await http.post("/api/customer/payments/stripe/initiate", {
        order_ids: [orderId],
        payment_method: "stripe",
      });
      const link = res?.data?.payment_link || res?.data?.data?.payment_link;
      if (!link) throw new Error("Stripe payment link not received");
      return { link, payment: res?.data?.payment };
    } else {
      const res = await http.post("/api/customer/payments/initiate", {
        order_ids: [orderId],
        payment_method: "paypal",
      });
      const data = res?.data?.data || res?.data;
      const link = data?.payment_link || data?.approval_url;
      if (!link) throw new Error("PayPal payment link not received");
      return { link, payment: data?.payment };
    }
  };

  // ── Pay Now ────────────────────────────────────────────
  // In PaymentPage — replace createBackendOrder usage
  const orderId = state?.orderId || guestOrder?.id || null;
  const createdOrder = state?.createdOrder || guestOrder || null;

  const handleProceedToPay = async () => {
    if (processing) return;
    if (items.length === 0) {
      toast.info("No items to pay for");
      return;
    }
    if (!orderId) {
      toast.error("Order not found. Please go back and try again.");
      navigate("/checkout", { state: { items } });
      return;
    }

    setProcessing(true);
    try {
      const { link, payment } = isGuest
        ? await initiateGuestPayment(orderId)
        : await initiateAuthPayment(orderId);

      sessionStorage.setItem(
        "pending_payment",
        JSON.stringify({
          orderId,
          createdOrder,
          totals: computedTotals,
          items,
          paymentMethod,
          isGuest,
          guestContact,
          selectedPromoCode,
          selectedVoucher,
          paymentId: payment?.id,
          createdAt: new Date().toISOString(),
        }),
      );

      toast.info(
        `Redirecting to ${paymentMethod === "stripe" ? "Stripe" : "PayPal"}…`,
      );
      window.location.href = link;
    } catch (e) {
      console.error("[PAYMENT]", e?.response?.data || e.message);
      toast.error(getApiMessage(e, "Payment failed. Please try again."));
      setProcessing(false);
    }
  };

  // ── Polling overlay ────────────────────────────────────
  if (polling) {
    return (
      <div className="pp-polling-overlay">
        <div className="pp-polling-card">
          <div className="pp-polling-spinner" />
          <div className="pp-polling-title">Confirming your payment…</div>
          <div className="pp-polling-sub">
            Please wait, this usually takes a few seconds.
          </div>
        </div>
      </div>
    );
  }

  // ── Confirmation screen ────────────────────────────────
  // Use confirmedIsGuest (from pending) NOT isGuest (from current authMode)
  if (orderConfirmed && orderData) {
    return (
      <OrderConfirmationScreen
        orderData={orderData}
        paymentData={paymentData}
        user={user}
        guestContact={confirmedGuestContact || guestContact}
        navigate={navigate}
        isGuest={confirmedIsGuest}
      />
    );
  }

  // ── Payment selection screen ───────────────────────────
  const activeMeta = PAYMENT_METHODS.find((m) => m.id === paymentMethod);

  return (
    <div>
      <div className="pp-page">
        <div className="pp-container">
          {isGuest && guestContact && (
            <div className="pp-guest-info">
              <span className="pp-guest-badge">👤 Guest Order</span>
              <span className="pp-guest-name">{guestContact.contact_name}</span>
              <span className="pp-guest-email">
                {guestContact.contact_email}
              </span>
            </div>
          )}

          <div className="pp-card">
            <h2 className="pp-section-title">Select Payment Method</h2>
            <div cmlassName="pp-methods">
              {PAYMENT_METHODS.map((method) => {
                const active = paymentMethod === method.id;
                return (
                  <div
                    key={method.id}
                    className={`pp-method ${active ? "active" : ""}`}
                    style={
                      active
                        ? { borderColor: method.color, background: method.bg }
                        : {}
                    }
                    onClick={() => !processing && setPaymentMethod(method.id)}
                  >
                    <div
                      className="pp-radio-dot"
                      style={
                        active
                          ? {
                              borderColor: method.color,
                              borderWidth: 5,
                              background: method.color,
                            }
                          : {}
                      }
                    />
                    <div className="pp-method-info">
                      <div className="pp-method-logo">{method.logo}</div>
                      <span className="pp-method-desc">
                        {method.description}
                      </span>
                    </div>
                    {active && (
                      <span
                        className="pp-method-badge"
                        style={{ background: method.color }}
                      >
                        Selected
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="pp-card">
            <h2 className="pp-section-title">Order Summary</h2>
            <div className="pp-summary-rows">
              <div className="pp-summary-row">
                <span>Items subtotal</span>
                <span>{toCurrency(computedTotals.itemsTotal)}</span>
              </div>
              <div className="pp-summary-row">
                <span>Shipping</span>
                <span>{toCurrency(computedTotals.deliveryFee)}</span>
              </div>
              {Number(computedTotals.promoDiscount) > 0 && (
                <div className="pp-summary-row discount">
                  <span>Promo discount</span>
                  <span>−{toCurrency(computedTotals.promoDiscount)}</span>
                </div>
              )}
              {Number(computedTotals.shippingVoucherDiscount) > 0 && (
                <div className="pp-summary-row discount">
                  <span>Voucher discount</span>
                  <span>
                    −{toCurrency(computedTotals.shippingVoucherDiscount)}
                  </span>
                </div>
              )}
            </div>
            <div className="pp-total-row">
              <span className="pp-total-label">Total</span>
              <span className="pp-total-value">
                {toCurrency(computedTotals.total)}
              </span>
            </div>
            <button
              className="pp-pay-btn"
              onClick={handleProceedToPay}
              disabled={processing}
              style={
                !processing
                  ? { background: activeMeta?.color || "#f77321" }
                  : {}
              }
            >
              {processing ? (
                <>
                  <span className="pp-btn-spinner" /> Redirecting…
                </>
              ) : (
                `Pay with ${activeMeta?.label} →`
              )}
            </button>
            <p className="pp-secure-note">
              🔒 Your payment is encrypted and secure
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PaymentPage;
